import React, { useState } from "react";
import { toast } from "sonner";
import { X, ThumbsUp, ThumbsDown, MessageSquare } from "lucide-react";
import { api } from "../lib/api";

// Persists whether feedback was already submitted
const DONE_KEY = (type, id) => `orello_fb_${type}_${id || "global"}`;
export function hasFeedback(type, id) {
  return !!localStorage.getItem(DONE_KEY(type, id));
}
export function markFeedback(type, id) {
  localStorage.setItem(DONE_KEY(type, id), "1");
}

const ONBOARDING_QS = [
  { key: "easy_registration",  text: "Was the registration process smooth and easy?" },
  { key: "kyc_clear",          text: "Was the KYC verification straightforward?" },
  { key: "platform_intuitive", text: "Did you find the platform easy to navigate?" },
  { key: "would_recommend",    text: "Would you recommend Orello to someone?" },
];

function buildCaseQs(hasDispute, hasManualReview) {
  return [
    { key: "checkout_fair",     text: "Was the checkout process handled fairly?" },
    { key: "settlement_clear",  text: "Was the deposit settlement transparent and clear?" },
    ...(hasDispute ? [
      { key: "ore_ai_fair",     text: "Was ORE-AI's dispute analysis fair and detailed?" },
    ] : []),
    ...(hasManualReview ? [
      { key: "manual_review_ok",text: "Was the Orello team's manual review satisfactory?" },
    ] : []),
    { key: "use_again",         text: "Would you use Orello again for your next rental?" },
  ];
}

export default function FeedbackModal({
  type,
  rentalId,
  hasDispute,
  hasManualReview,
  onClose,       // called when user closes without submitting
  onSubmitted,   // called after successful submit
}) {
  const [answers, setAnswers]   = useState({});
  const [comments, setComments] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy]         = useState(false);

  const questions  = type === "onboarding" ? ONBOARDING_QS : buildCaseQs(hasDispute, hasManualReview);
  const allAnswered = questions.every(q => answers[q.key] !== undefined);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/feedback", {
        type,
        rental_id: rentalId || null,
        answers: questions.map(q => ({ question: q.text, answer: answers[q.key] })),
        comments: comments.trim(),
      });
      markFeedback(type, rentalId);
      setSubmitted(true);
    } catch {
      toast.error("Failed to submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleDone = () => {
    if (onSubmitted) onSubmitted();
    else if (onClose) onClose();
  };

  // ── Thank-you screen ──────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" onClick={handleDone}>
        <div className="card-base w-full max-w-sm p-8 text-center space-y-4" onClick={e => e.stopPropagation()}>
          {/* Close × */}
          <div className="flex justify-end -mt-2 -mr-2">
            <button onClick={handleDone}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="text-5xl">🙏</div>
          <h3 className="text-lg font-bold font-display" style={{ color: "var(--dash-text)" }}>
            Thank you!
          </h3>
          <p className="text-sm text-gray-500">Your feedback helps us keep improving Orello for everyone.</p>
          <button onClick={handleDone}
            className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-medium py-2.5 rounded-lg text-sm transition-colors">
            Done
          </button>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" onClick={onClose}>
      <div className="card-base w-full max-w-md max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="h-4 w-4 text-[#F97316]" />
            </div>
            <div>
              <div className="font-semibold text-sm" style={{ color: "var(--dash-text)" }}>
                {type === "onboarding" ? "How was your onboarding?" : "Rate your overall experience"}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">Takes 30 seconds · all answers help us improve</div>
            </div>
          </div>
          {/* Prominent close button */}
          <button
            onClick={onClose}
            aria-label="Close feedback"
            className="ml-3 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {questions.map((q, i) => (
            <div key={q.key}>
              <p className="text-sm font-medium mb-2.5" style={{ color: "var(--dash-text)" }}>
                {i + 1}. {q.text}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setAnswers(a => ({ ...a, [q.key]: true }))}
                  className={`flex items-center gap-1.5 px-5 py-2 rounded-lg border text-sm font-medium transition-all ${
                    answers[q.key] === true
                      ? "bg-emerald-500 border-emerald-500 text-white shadow-sm"
                      : "border-gray-200 text-gray-600 hover:border-emerald-300 hover:bg-emerald-50"
                  }`}
                >
                  <ThumbsUp className="h-3.5 w-3.5" /> Yes
                </button>
                <button
                  onClick={() => setAnswers(a => ({ ...a, [q.key]: false }))}
                  className={`flex items-center gap-1.5 px-5 py-2 rounded-lg border text-sm font-medium transition-all ${
                    answers[q.key] === false
                      ? "bg-red-500 border-red-500 text-white shadow-sm"
                      : "border-gray-200 text-gray-600 hover:border-red-300 hover:bg-red-50"
                  }`}
                >
                  <ThumbsDown className="h-3.5 w-3.5" /> No
                </button>
              </div>
            </div>
          ))}

          {/* Comments */}
          <div>
            <label className="text-sm font-medium" style={{ color: "var(--dash-text)" }}>
              Any comments? <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={comments}
              onChange={e => setComments(e.target.value)}
              rows={3}
              placeholder="What went well? What can we improve?"
              className="mt-1.5 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-100 focus:border-[#F97316]"
            />
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex-shrink-0 space-y-2.5">
          <button
            onClick={submit}
            disabled={busy || !allAnswered}
            className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-medium py-2.5 rounded-lg text-sm disabled:opacity-40 transition-colors"
          >
            {busy ? "Submitting…" : allAnswered ? "Submit Feedback" : `Answer all ${questions.length} questions to submit`}
          </button>
          <button
            onClick={onClose}
            className="w-full text-gray-400 text-xs py-1.5 hover:text-gray-600 transition-colors"
          >
            Close — I'll skip this time
          </button>
        </div>
      </div>
    </div>
  );
}
